import { autoinject, bindable, computedFrom, Disposable, observable } from 'aurelia-framework';
import { EventAggregator } from 'aurelia-event-aggregator';
import { ServiceStructureService, ServiceNodeModel, ServiceSelections } from '../services/service-structure-service';
import { NodeModel, NodeType, flattenTree, concatChildren, findTreenodeById } from '../../models/node-model';
import { GlobalContextService } from '../../components/global-select/global-context-service';
import { ICustomer } from '../customers/customer-service';
import { ScopeService, IScopeAM } from '../scopes/scope-service';
import { MeasureService, IMeasureInputAM, IMissingMeasureEntriesAM, IMeasureEntryAM, MeasureEntryAM } from './services/measure-service';
import { LinkedMappingsService, CostCenterServiceLink } from '../services/service-targetings/linked-mappings-service';
import { AppSettings } from '../../utils/appsettings';
import { GlobalCustomerSelect } from '../../components/global-select/customer/global-customer-select';
import { GlobalScopeSelect } from '../../components/global-select/scope/global-scope-select';
import { includes, orderBy, find, isEqual, flatten, uniqWith, uniqBy } from 'lodash';
import { ResourceService } from '../../utils/resourceService';
import {CustomerMunicipality, MunicipalityService} from "../municipalities/municipality-service";
import { IFormItem, MeasureInputGroup } from './measure-input-group';
import { OrganizationalNodeModel, OrganizationalUnitService } from '../customers/organizational-units/organizational-unit-service';

@autoinject
export class MeasuresInputContainer {
	selectedServiceSubscriber: Disposable;
	activeScopeSubscriber: Disposable;
	activeCustomerSubscriber: Disposable;
	measureInputs: IMeasureInputAM[];
	comparisonMeasureInputs: IMeasureInputAM[];
	servicesToShow: NodeModel[];
	servicesToCollectDataFor: NodeModel[];
	costCenterLinks: CostCenterServiceLink[];
	showMeasureInputs: boolean;
	updatingMeasures: boolean = false;
	@observable switchState = 0;
	@observable selectedStructure: 'Organizational' | 'Service' = 'Organizational';
	
	@observable serviceStructureNodes: ServiceNodeModel[] = null;
	@observable organizationalStructureNodes: OrganizationalNodeModel[] = null;
	@bindable selectedNode: ServiceNodeModel;
	@bindable showInputsForCostCenters: boolean = false;

	scope: IScopeAM;
	@observable comparisonScope: IScopeAM;
	potentialComparisonScopes: IScopeAM[];
	initializingScope: boolean;

	@observable customerMunicipalities: CustomerMunicipality[];
	private comparisonCustomerMunicipalities: CustomerMunicipality[];

	partialMemberServiceSelections: { [customerMunicipalityId: string]: string[] } = { }; // key: customerMunicipalityId, value: selected service-id's

	private customerItemName = GlobalCustomerSelect.customerItemName;
	private scopeItemName = GlobalScopeSelect.scopeItemName;	

	@bindable measureInputGroups: MeasureInputGroup[] = [];
	@bindable searchTerm: string;

	constructor(
		private serviceStructureService: ServiceStructureService,
		private organizationalUnitService: OrganizationalUnitService,
		private linkedMappingsService: LinkedMappingsService,
		private scopeService: ScopeService,
		private municipalityService: MunicipalityService,
		private element: Element,
		private measureService: MeasureService,
		private globalContext: GlobalContextService,
		private ea: EventAggregator,
		private resourceService: ResourceService) {

		this.measureInputs = [];
		this.showMeasureInputs = false;
	}

	attached() {
		this.bindEventListeners();
		this.initializeScope();
	}

	detached() {
		this.selectedServiceSubscriber.dispose();
		this.activeScopeSubscriber.dispose();
		this.activeCustomerSubscriber.dispose();
	}

	switchStateChanged(newState): void {
		if (newState === 1) {
			this.measureService.getServicesMissingMeasures(this.scope.id)
				.then(data => this.onlyShowNodesWithMissingEntries(data));
		} else {
			if (this.serviceStructureNodes) {
				this.serviceStructureNodes.forEach(n => n.traverseTree(node => node.isHidden = false));
			}
		}
	}

	get isOrganizationalStructure(): boolean {
		return this.selectedStructure == 'Organizational';
	}

	get isServiceStructure(): boolean {
		return this.selectedStructure == 'Service';
	}

	@computedFrom('customerId')
	get activeCustomer(): ICustomer {
		return this.globalContext.getItem(this.customerItemName);
	}

	get customerId(): string {
		return this.globalContext.getId(this.customerItemName);
	}

	@computedFrom('scope', 'customerId')
	get activeScope(): IScopeAM {
		return this.scope;
	}

	@computedFrom('activeScope')
	get serviceStructureName(): string {
		if (this.activeScope) {
			return this.activeScope.serviceStructureName;
		}
		return null;
	}

	@computedFrom('activeScope')
	get serviceStructureId(): string {
		if (this.activeScope) {
			return this.activeScope.serviceStructureId;
		}
		return null;
	}

	@computedFrom('activeScope')
	get fiscalYearId(): string {
		if (this.activeScope) {
			return this.activeScope.fiscalYearId;
		}
		return null;
	}
	
	@computedFrom('selectedNode')
	get selectedNodeIsCostCenter(): boolean {
		return this.selectedNode && this.selectedNode.nodeType === NodeType.CostCenter;
	}

	@computedFrom('selectedNode', 'comparisonScope', 'comparisonMeasureInputs')
	get showComparisonMeasuresBulkCopy() : boolean {
		return !this.scope.isClosed && this.selectedNode && this.comparisonScope && this.comparisonMeasureInputs && this.comparisonMeasureInputs.length > 0;
	}

	async refreshMunicipalities(): Promise<any> {
		this.customerMunicipalities = await this.municipalityService.getMunicipalitiesForFiscalYear(this.scope.fiscalYearId);
	}

	async refreshPartialMemberServiceSelections(): Promise<any> {

		const partialMemberIds = this.customerMunicipalities.filter(cm => cm.type == "PartialMember").map(cm => cm.customerMunicipalityId);
		const serviceSelections: ServiceSelections =
			await this.serviceStructureService.getSelectedServicesForFiscalYear(this.customerId, this.scope.fiscalYearId, this.serviceStructureId, partialMemberIds);
		this.partialMemberServiceSelections = serviceSelections.forPartialMembers;
	}

	changeStructureType(structureType: 'Organizational' | 'Service') {
		this.showMeasureInputs = false;
		this.searchTerm = "";
		this.selectedStructure = structureType;
	}

	changeStructureTypeKeyboard(e: KeyboardEvent, structureType: 'Organizational' | 'Service') {
		e.stopPropagation();

		if (e.key === "Enter") {
			this.changeStructureType(structureType);
		}

		return true;
	}

	// Disable service level inputs for a service if they are already shown
	disableFormItemServiceLevelInputs(group: MeasureInputGroup, formItem: IFormItem) {
		group.formItems.forEach(item => { 
			if (item.serviceId === formItem.serviceId && item.showInputsForServiceLevel) {
				item.showInputsForServiceLevel = false;
			}
		});
	}

	// Group selected nodes based on type
	private refreshInputGroups() {
		this.measureInputGroups = [];
		
		switch (this.selectedNode.nodeType) {
			case NodeType.Service: {

				if (this.selectedStructure == 'Service') {
					this.selectedNode.traverseTree(node => { 

						// Create input group for each service node for service level measures
						// This group contains a single FormItem (the service itself)
						if (node.nodeType == NodeType.Service) {

							const showInputsForServiceLevel = true;

							const inputGroup = new MeasureInputGroup(node, this.selectedStructure, showInputsForServiceLevel);
							inputGroup.formItems = this.servicesToShow.filter(service => service.id === node.id).map(service => {
								return {
									title: node.title,
									showInputsForServiceLevel: showInputsForServiceLevel,
									serviceId: service.id,
									costCenterId: null,
									dimensionValueIds: [],
								}
							});								
							this.measureInputGroups.push(inputGroup);
						}

						// Create input group for each service node that has cost centers links
						// This groups contains all cost centers as FormItems
						if (this.showInputsForCostCenters && this.costCenterLinks.filter(link => link.serviceId === node.id && link.collectMeasures).length > 0) {

							const showInputsForServiceLevel = false;

							const inputGroup = new MeasureInputGroup(node, this.selectedStructure, showInputsForServiceLevel);
							node.children.map(child => child as ServiceNodeModel).forEach(child => {
								if (child.nodeType == NodeType.CostCenter) {
									const formItems = this.servicesToShow.filter(service => service.id === node.id).map(service => {
										return {
											title: child.title,
											showInputsForServiceLevel: showInputsForServiceLevel,
											serviceId: service.id,
											costCenterId: child.id,
											dimensionValueIds: child.dimensionValueIds,
										}
									});
									inputGroup.formItems = [...inputGroup.formItems, ...formItems];
								}
							});
							
							if (inputGroup.formItems.length > 0) {
								this.measureInputGroups.push(inputGroup);
							}
						}
					});
				} else {
					const inputGroup = new MeasureInputGroup(this.selectedNode.parent, this.selectedStructure, false);

					const formItems = this.servicesToShow.filter(service => service.id === this.selectedNode.id).map(service => {
						return {
							title: service.title,
							showInputsForServiceLevel: false,
							serviceId: service.id,
							costCenterId: this.selectedNode.parent.id,
							dimensionValueIds: this.selectedNode.dimensionValueIds,
						}
					});
					inputGroup.formItems = formItems;

					this.measureInputGroups.push(inputGroup);
				}
				break;
			}
			case NodeType.CostCenter: {

				const showInputsForServiceLevel = false

				if (this.selectedStructure == 'Service') {
					if (this.costCenterLinks.filter(link => link.costCenterId === this.selectedNode.id && link.collectMeasures).length > 0) {
						const inputGroup = new MeasureInputGroup(this.selectedNode, this.selectedStructure, showInputsForServiceLevel);

						const formItems = this.servicesToShow.filter(service => service.id === this.selectedNode.parent.id).map(service => {
							return {
								title: service.title,
								showInputsForServiceLevel: showInputsForServiceLevel,
								serviceId: service.id,
								costCenterId: this.selectedNode.id,
								dimensionValueIds: this.selectedNode.dimensionValueIds,
							}
						});
						inputGroup.formItems = formItems;
				
						this.measureInputGroups.push(inputGroup);
						break;
					}
				} else {
					if (this.selectedNode.children.length > 0) {
						const inputGroup = new MeasureInputGroup(this.selectedNode, this.selectedStructure, showInputsForServiceLevel);

						this.selectedNode.children.map(child => child as ServiceNodeModel).forEach(child => {
							if (child.nodeType == NodeType.Service) {
								const formItem = {
									title: child.title,
									showInputsForServiceLevel: showInputsForServiceLevel,
									serviceId: child.id,
									costCenterId: this.selectedNode.id,
									dimensionValueIds: child.dimensionValueIds,
								}
								inputGroup.formItems.push(formItem);
							}
						});

						this.measureInputGroups.push(inputGroup);
						break;
					}
				}
			}
			case NodeType.OrganizationalUnit: {

				const showInputsForServiceLevel = false;

				this.selectedNode.traverseTree(node => {
					if (node.nodeType == NodeType.CostCenter && node.children.length > 0) {
						const inputGroup = new MeasureInputGroup(node, this.selectedStructure, showInputsForServiceLevel);

						node.children.map(child => child as ServiceNodeModel).forEach(child => {
							if (child.nodeType == NodeType.Service) {
								const formItem = {
									title: child.title,
									showInputsForServiceLevel: showInputsForServiceLevel,
									serviceId: child.id,
									costCenterId: node.id,
									dimensionValueIds: child.dimensionValueIds,
								}
								inputGroup.formItems.push(formItem);
							}
						});

						this.measureInputGroups.push(inputGroup);
					}
				});
				break;
			}
			default:
				throw Error(`Invalid node type of ${this.selectedNode.nodeType} in tree!`);
		}
	}

	async selectedNodeChanged() {
		this.showMeasureInputs = false;
		if (this.selectedNode) {
			const result = await this.getMeasureValues(this.activeScope.id);
			this.measureInputs = result;
			
			this.refreshSelectedServices(result);
			this.refreshComparisonData();
			this.refreshInputGroups();
			
			this.showMeasureInputs = true;
		}
	}

	async showInputsForCostCentersChanged() {
		this.showMeasureInputs = false;
		if (this.selectedNode) {
			const result = await this.getMeasureValues(this.activeScope.id);
			this.measureInputs = result;
			
			this.refreshSelectedServices(result);
			this.refreshComparisonData();
			this.refreshInputGroups();
			
			this.showMeasureInputs = true;
		}
	}

	async comparisonScopeChanged(newValue) : Promise<any> {

		this.comparisonCustomerMunicipalities = this.comparisonScope
			? await this.municipalityService.getMunicipalitiesForFiscalYear(this.comparisonScope.fiscalYearId)
			: null;
		this.refreshComparisonData();
	}

	refreshComparisonData() {
		if(this.selectedNode && this.comparisonScope) {
			this.getMeasureValues(this.comparisonScope.id).then(result => {

				const relevantCustomerMunicipalityIdsInComparisonScope =
					this.customerMunicipalities.map(
						cm => this.getComparisonCustomerMunicipalityId(cm.customerMunicipalityId)
					);
				
				this.comparisonMeasureInputs = result.filter(mi =>
					mi.existingEntries.length > 0 &&
					this.servicesToCollectDataFor.some(ms => ms.id === mi.serviceId));

				this.comparisonMeasureInputs.forEach(comparisonInput => {

					const inputForSelectedScope = this.measureInputs.find(m =>
						m.serviceId === comparisonInput.serviceId &&
						m.measureId === comparisonInput.measureId);

					comparisonInput.existingEntries = comparisonInput.existingEntries.filter(me =>
						inputForSelectedScope &&
						MeasureEntryAM.getInstance(me).isEntryMeasured(inputForSelectedScope)
						&& (
							!me.customerMunicipalityId ||
							includes(relevantCustomerMunicipalityIdsInComparisonScope, me.customerMunicipalityId)
						)
					);
				});
				
				this.comparisonMeasureInputs = this.comparisonMeasureInputs.filter(mi => mi.existingEntries.length > 0);

				// Filter duplicate serviceId & measureId combinations from inputs
				this.comparisonMeasureInputs = uniqWith(this.comparisonMeasureInputs, (a, b) => isEqual(a.measureId + a.serviceId, b.measureId + b.serviceId));
			});
		}
		else {
			this.comparisonMeasureInputs = [];
		}
	}
	
	getComparisonCustomerMunicipalityId(customerMunicipalityId: string): string {
		
		if (customerMunicipalityId == null || !this.comparisonScope) { return null; }
		
		const customerMunicipality = this.customerMunicipalities.find(cm => cm.customerMunicipalityId === customerMunicipalityId);
		const comparisonCustomerMunicipality = this.comparisonCustomerMunicipalities.find(ccm => ccm.municipalityId === customerMunicipality.municipalityId);
		if (!comparisonCustomerMunicipality) { return null; }
		return comparisonCustomerMunicipality.customerMunicipalityId;
	}

	bindEventListeners() {
		this.activeScopeSubscriber = this.ea.subscribe(`itemChanged-${this.scopeItemName}`, async (scope: IScopeAM) => {
			this.initializingScope = false;
			this.initializeScope();
		});

		this.activeCustomerSubscriber = this.ea.subscribe(`itemChanged-${this.customerItemName}`, async (customer: any) => {
			this.initializingScope = false;
			this.initializeScope();
		});
	}

	async initializeScope() {

		if (this.initializingScope) {
			return;
		}

		this.scope = this.globalContext.getItem(this.scopeItemName);
		this.initializingScope = true;
		
		if (this.scope && this.serviceStructureId && this.fiscalYearId) {
			
			this.showMeasureInputs = false;
			await this.refreshMunicipalities();
			await this.refreshPartialMemberServiceSelections();

			await this.linkedMappingsService.refreshLinksCache(this.fiscalYearId, this.serviceStructureId);
			this.costCenterLinks = await this.linkedMappingsService.fetchLinksForFiscalYear(this.fiscalYearId, this.scope.serviceStructureId);

			await this.refreshServiceStructureTree();
			await this.refreshOrganizationalStructureTree();

			this.potentialComparisonScopes = (await this.scopeService.getScopesForCustomer(this.customerId)).filter(
				s => s.id !== this.scope.id && s.serviceStructureId === this.scope.serviceStructureId);
			this.comparisonScope = null;
		}
		else {
			this.showMeasureInputs = false;
			this.potentialComparisonScopes = null;

			await this.refreshServiceStructureTree();
			await this.refreshOrganizationalStructureTree();
		}

		this.initializingScope = false;
	}

	async refreshServiceStructureTree(): Promise<any> {
		this.serviceStructureNodes = null;

		if (!this.fiscalYearId) {
			return;
		}

		// Service nodes
		const serviceNodes = await this.serviceStructureService.getServiceStructureForFiscalYear(this.scope.serviceStructureId, this.fiscalYearId);
		if (!serviceNodes) { return; }

		serviceNodes.forEach(service => {
			service.traverseTree(node => {
				const costCenterLinksForService: NodeModel[] = this.costCenterLinks.filter(
					link => link.serviceId === node.id && link.collectMeasures).map(
					link => {
						const childNode = new ServiceNodeModel(link.toString(), 0, node.title, link.costCenterId, link.identifier);
						childNode.nodeType = NodeType.CostCenter;
						childNode.parent = node;
						childNode.dimensionValueIds = link.dimensionValueIds;
						return childNode;
					});
				
				// First cost centers, then sub-services
				node.children = costCenterLinksForService.concat(node.children);
			});
		});

		this.serviceStructureNodes = serviceNodes;
	}

	async refreshOrganizationalStructureTree(): Promise<any> {
		if (!this.fiscalYearId) {
			return;
		}

		// Org nodes
		const organizationalNodes = await this.organizationalUnitService.getOrganizationalUnitsForFiscalYear(this.fiscalYearId, false);
		if (!organizationalNodes) { return; }

		organizationalNodes.forEach(ou => {
			ou.traverseTree(node => {
				const serviceLinksForCostCenter: NodeModel[] = this.costCenterLinks
					.filter(link => link.costCenterId == node.id && link.collectMeasures)
					.map(link => {

						// Find sort order for service from ServiceStructureTree
						const serviceNode = findTreenodeById<ServiceNodeModel>(this.serviceStructureNodes, link.serviceId);

						const childNode = new ServiceNodeModel(link.formatServiceNameWithDimensionSuffix(), serviceNode?.sortOrder, node.title, link.serviceId, serviceNode?.code);
						childNode.nodeType = NodeType.Service;
						childNode.parent = node;
						childNode.level = serviceNode?.parent?.sortOrder; // Used to store the sortOrder of the parent service in ServiceStructure for sorting purposes
						childNode.dimensionValueIds = link.dimensionValueIds; // DimensionValueIds of the linked CostCenter
						return childNode;
					});

				node.children = serviceLinksForCostCenter.concat(node.children);

				// sort node.children by level and then by sortOrder to match order in ServiceStructure
				node.children = orderBy(node.children, ['level', 'sortOrder'], ['asc', 'asc']);
			});
		})

		this.organizationalStructureNodes = organizationalNodes;
	}

	async getMeasureValues(scopeId: string) : Promise<IMeasureInputAM[]> {
		this.updatingMeasures = true;
		let measures: IMeasureInputAM[] = [];

		switch (this.selectedNode.nodeType) {
			// If selected node is of type 'Service', fetch measure inputs for single service
			case NodeType.Service: {
				const costCenterId = null;
				const serviceId = this.selectedNode.id;
				const dimensionValueIds = [];
				measures = await this.measureService.getMeasureInputs(scopeId, serviceId, costCenterId, dimensionValueIds);
				break;
			}
			// If selected node is of type 'CostCenter', fetch measure inputs for all services targeted to the cost center
			case NodeType.CostCenter: {
				if (this.selectedStructure == 'Service') {
					const costCenterId = this.selectedNode.id;
					const serviceId = this.selectedNode.parent.id;
					const dimensionValueIds = this.selectedNode.dimensionValueIds;
					measures = await this.measureService.getMeasureInputs(scopeId, serviceId, costCenterId, dimensionValueIds);
				} else {
					// All children are of type 'Service'	
					const serviceIds = this.selectedNode.children.map(service => service.id);
					measures = await this.measureService.getMeasureInputsForServices(scopeId, serviceIds);
				}
				break;
			}
			// If selected node is of type 'OrganizationalUnit', fetch measure inputs for all services for all cost centers within the unit
			case NodeType.OrganizationalUnit: {
				const serviceIds: string[] = [];
				this.selectedNode.traverseTree(node => {
					if (node.nodeType == 'CostCenter') {
						const serviceIdsForCostCenter = node.children.map(service => service.id);
						serviceIds.push(...serviceIdsForCostCenter);
					}
				});
				
				measures = await this.measureService.getMeasureInputsForServices(scopeId, serviceIds);
				break;
			}
			default:
				throw Error(`Invalid node type of ${this.selectedNode.nodeType} in tree!`);
		}

		this.updatingMeasures = false;
		return measures;
	}

	refreshSelectedServices(measures: IMeasureInputAM[]) {
		let selectedServices: NodeModel[] = [];

		switch (this.selectedNode.nodeType) {
			case NodeType.Service: {
				if (this.selectedStructure == 'Service') {
					selectedServices = flattenTree([this.selectedNode]).filter(service => measures?.some((item) => item.serviceId === service.id));
				} else {
					selectedServices = [this.selectedNode];
				}
				break;
			}
			case NodeType.CostCenter: {
				if (this.selectedStructure == 'Service') {	
					selectedServices = [this.selectedNode.parent];
				} else {
					selectedServices = flattenTree([...this.selectedNode.children]);				
				}
				break;
			}
			case NodeType.OrganizationalUnit: {
				this.selectedNode.traverseTree(node => {
					if (node.nodeType == NodeType.Service) {
						selectedServices.push(node);
					}
				});
				break;
			}
			default:
				throw Error(`Invalid node type of ${this.selectedNode.nodeType} in tree!`);
		}
		
		this.servicesToShow = this.filterServicesToShow(selectedServices, measures, true);
		this.servicesToCollectDataFor = this.filterServicesToShow(selectedServices, measures, false);
	}

	filterServicesToShow(services: NodeModel[], measureInputs: IMeasureInputAM[], includeNonCollectedServicesHavingData: boolean): NodeModel[] {

		return services.filter(service => measureInputs.some(inp =>
			inp.serviceId === service.id && (
				inp.forServiceTotal ||
				inp.forServiceByMunicipalities ||
				(
					(inp.forCostCentersTotal || inp.forCostCentersByMunicipalities) &&
					findTreenodeById<ServiceNodeModel>(this.serviceStructureNodes, inp.serviceId).children.filter(child => child.nodeType == NodeType.CostCenter).length > 0
				) ||
				(includeNonCollectedServicesHavingData && inp.existingEntries.length > 0)
			)
		));
	}

	// TODO: Jos puiden filtteröintiä joskus otetaan takaisin käyttöön (SwitchState + sen toiminnallisuus)
	// tämä metodi pitäisi tsekata kanssa kuntoon sen suhteen, että miten se käyttäytyy eri puuhierarkioiden kanssa
	private onlyShowNodesWithMissingEntries(missing: IMissingMeasureEntriesAM) {
		this.serviceStructureNodes.forEach(n => {

			n.traverseTree((node: NodeModel) => node.isHidden = true);

			n.traverseTree((node: NodeModel) => {

				if (node.nodeType === NodeType.CostCenter) {
					if (!missing.costCenterIds.find(id => id === node.id)) {
						return;
					}
				} else {
					if (!missing.serviceIds.find(id => id === node.id)) {
						return;
					}
				}

				node.isHidden = false;
				node.traverseParents(parent =>
					parent.isHidden = false
				);
			});
		});
	}

	async confirmComparisonScopeCopy() {

		const existingEntries = this.comparisonMeasureInputs.map(mi => mi.existingEntries).flat();
		const count = this.selectedStructure == 'Organizational' && this.selectedNode.nodeType != NodeType.OrganizationalUnit ?
			existingEntries.filter(e => this.selectedNodeIsCostCenter ? e.costCenterId == this.selectedNode.id : e.costCenterId == this.selectedNode.parent.id).length : // Filter count for correct cost center
			existingEntries.length;

		const confirmCopyMessage = this.resourceService.getResource(`MeasuresInputContainer_Confirm_CopyMeasureEntriesToScope`, [count.toString()]);

		if (confirm(confirmCopyMessage)) {

			const existingValues : boolean = this.measureInputs.filter(mi => mi.existingEntries.length > 0).length > 0;
			var confirmReplaceMessage = this.resourceService.getResource('MeasuresInputContainer_Confirm_MeasuresAlreadyExist');

			if (!existingValues || confirm(confirmReplaceMessage)) {

				if (this.selectedStructure == 'Service') {

					const serviceId = this.selectedNodeIsCostCenter ? this.selectedNode.parent.id : this.selectedNode.id;
					const costCenterId = this.selectedNodeIsCostCenter ? this.selectedNode.id : null;					
					await this.measureService.copyMeasureEntriesToScope(serviceId, this.customerId, this.scope.id, this.comparisonScope.id, costCenterId);
					this.selectedNodeChanged();

				} else {

					let updateRequests: Promise<any>[] = [];
					this.measureInputGroups.forEach(group => {
						const proms = group.formItems.map(async (item) => await this.measureService.copyMeasureEntriesToScope(item.serviceId, this.customerId, this.scope.id, this.comparisonScope.id, item.costCenterId))
						updateRequests = updateRequests.concat(proms);
					});
					await Promise.all(updateRequests);
					this.selectedNodeChanged();

				}
			}
		}
	}

	focusTreeView() {
		this.ea.publish('focus-tree-view');
	}
}
